/**
 * Time sheets for one person over a date range (web Time): hours per job per day — on the clock (clock sessions,
 * what BattleBooks is fed) or on tasks (task time entries) — the task breakdown inside a job, and gross pay (hours ×
 * rate, no payroll deductions). Pay weeks start on Sunday unless set otherwise. Pure.
 */
const DAY_MS = 86400000;
const dayNum = (d) => Math.floor(Date.parse(`${d}T12:00:00Z`) / DAY_MS);
const addDays = (d, n) => new Date((dayNum(d) + n) * DAY_MS).toISOString().slice(0, 10);
const localDate = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** The first day of the pay week holding `date` (startDay: 0 = Sunday … 6 = Saturday). */
export function payWeekStart(date, startDay = 0) {
  const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
  return addDays(date, -((dow - startDay + 7) % 7));
}

/** Every date from → to inclusive (max 400). */
export function rangeDays(from, to) {
  const out = [];
  for (let d = from; d <= to && out.length < 400; d = addDays(d, 1)) out.push(d);
  return out;
}

const isWork = (x) => x && !x.overlay && x.type !== 'travel' && !x.migrated;
const sessionOf = (x, uid, me) => (x.ownerUid || me) === uid;
/** A task's time is its assignee's, or (unassigned) its owner's. */
const taskOf = (t, uid, me) => (t.assignee ? t.assignee === uid : (t.ownerUid || me) === uid);

/**
 * Hours per job per day for `uid` in [from, to]. mode 'clock' = clock sessions, 'tasks' = task time entries.
 * → { days, rows: [{ projectId, byDay: { date: ms }, total }], byDay, total } (rows biggest first; 'none' = no job).
 */
export function timeSheet({ sessions = [], tasks = [], uid, me = null, from, to, mode = 'clock', now = Date.now() }) {
  const days = rangeDays(from, to);
  const inRange = new Set(days);
  const rows = new Map();
  const byDay = {};
  const add = (pid, date, ms) => {
    if (!inRange.has(date) || !(ms > 0)) return;
    const r = rows.get(pid) || { projectId: pid, byDay: {}, total: 0 };
    r.byDay[date] = (r.byDay[date] || 0) + ms; r.total += ms; rows.set(pid, r);
    byDay[date] = (byDay[date] || 0) + ms;
  };
  if (mode === 'clock') {
    for (const x of sessions) if (isWork(x) && sessionOf(x, uid, me)) add(x.projectId || 'none', x.date || localDate(x.startedAt), (x.endedAt || now) - x.startedAt);
  } else {
    for (const t of tasks) if (taskOf(t, uid, me)) for (const e of t.timeEntries || []) add(t.projectId || 'none', e.date, Number(e.duration) || 0);
  }
  const list = [...rows.values()].sort((a, b) => b.total - a.total);
  return { days, rows: list, byDay, total: list.reduce((m, r) => m + r.total, 0) };
}

/** Inside one job: each task's time for `uid` in [from, to] (task time entries), biggest first. */
export function jobBreakdown({ tasks = [], projectId, uid, me = null, from, to }) {
  const out = [];
  for (const t of tasks) {
    if ((t.projectId || 'none') !== projectId || !taskOf(t, uid, me)) continue;
    const byDay = {}; let total = 0;
    for (const e of t.timeEntries || []) {
      if (!e.date || e.date < from || e.date > to) continue;
      const ms = Number(e.duration) || 0;
      if (ms <= 0) continue;
      byDay[e.date] = (byDay[e.date] || 0) + ms; total += ms;
    }
    if (total) out.push({ task: t, byDay, total });
  }
  return out.sort((a, b) => b.total - a.total);
}

/** Gross pay for `ms` at `rate` $/h, to the cent (no deductions). */
export const grossPay = (ms, rate) => Math.round(((Number(ms) || 0) / 3600000) * (Number(rate) || 0) * 100) / 100;
